/**
 * "With Matthew moving into aged care" (owner, 24 Oct 2026: "it should clearly call this out — with
 * Matthew moving into aged care, the following assets were used, and this is what's left over ... a
 * client shouldn't in theory need to open up the financial modelling to try and work out whether it's
 * been done correctly or not").
 *
 * Read off the two households the figures use — the profile as entered, and the household once the
 * chosen aged care plan has been carried out (useSahBaseline's linkedCare) — so it says what the
 * modelling did, not what it was meant to do. One derivation (`linkedCareStory`) for step 2, the plan
 * page and the advice document.
 */
import React from 'react';
import { formatCurrency } from '../../utils/index.ts';
import { savedPlanTitle } from '../agedCare/planWords.ts';
import { sahEnteringPerson } from './sahEngineContext.ts';
import type { SahLinkedCare } from './useSahBaseline.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

const money = (n: number) => formatCurrency(Math.round(Math.abs(n)));

/** Plain names for the asset groups, as a client would say them. */
const GROUP_WORDS: Record<string, string> = {
    bankAccounts: 'Bank account',
    termDeposits: 'Term deposit',
    shares: 'Shares',
    investmentBonds: 'Investment bond',
    superannuation: 'Super',
    accountBasedPensions: 'Account-based pension',
    annuities: 'Lifetime income (annuity)',
    funeralBonds: 'Funeral bond',
    prepaidFuneral: 'Prepaid funeral',
    loansToOthers: 'Money lent to family',
    personalAssets: 'Car and contents',
    investmentProperty: 'Investment property',
    other: 'Other',
};
/** Groups that are not savings the household draws on, or are the deposit itself. */
const NOT_LEFT = new Set(['radPaid', 'personalAssets']);

type Row = { description?: string; value?: unknown; ownership?: string };
interface Item { key: string; label: string; group: string; value: number }

const num = (v: unknown) => parseFloat(String(v ?? 0)) || 0;

function itemsOf(ff: DraftFactFind, names: { client: string; partner: string }): Item[] {
    const assets = ((ff?.assets || {}) as Record<string, Row[] | undefined>);
    const raw: Item[] = [];
    // Matched before and after by group, name and owner (and the nth of those), not by position: a plan
    // can drop a sold asset from the list.
    const seen = new Map<string, number>();
    for (const [group, list] of Object.entries(assets)) {
        if (!Array.isArray(list)) continue;
        list.forEach((r) => {
            const base = String(r?.description || GROUP_WORDS[group] || group).trim();
            const id = `${group}|${base}|${r?.ownership ?? ''}`;
            const nth = seen.get(id) || 0;
            seen.set(id, nth + 1);
            raw.push({ key: `${id}|${nth}`, label: base, group, value: num(r?.value) });
        });
    }
    // Two accounts with the same name say whose they are (owner, 22 Oct 2026: "MLC ABP, MLC ABP").
    const counts = new Map<string, number>();
    raw.forEach((it) => counts.set(it.label, (counts.get(it.label) || 0) + 1));
    return raw.map((it) => {
        if ((counts.get(it.label) || 0) < 2) return it;
        const own = it.key.split('|')[2];
        const who = own === 'client' ? names.client : own === 'partner' ? names.partner : own === 'joint' ? 'joint' : '';
        return who ? { ...it, label: `${it.label} (${who === 'joint' ? 'joint' : `${who}'s`})` } : it;
    });
}

const debtsOf = (ff: DraftFactFind) => Object.values(((ff?.liabilities || {}) as Record<string, Row[] | undefined>))
    .flatMap((list) => (Array.isArray(list) ? list : []))
    .reduce((t, r) => t + num((r as { balance?: unknown }).balance ?? r.value), 0);

export interface LinkedCareStory {
    enteringName: string;
    stayingName: string;
    planTitle: string;
    whole: boolean;
    reason?: string;
    caveats: string[];
    deposit: number;
    /** What the plan drew down or sold, with its balance before and after. */
    used: Array<{ label: string; before: number; after: number }>;
    /** What the plan put in place (a funeral bond, a lifetime income). */
    added: Array<{ label: string; value: number }>;
    debtsCleared: number;
    homeSold: boolean;
    fees: number;
    living: number | null;
    /** The savings left for the one at home, after the plan. */
    left: Array<{ label: string; value: number }>;
    leftTotal: number;
}

export function linkedCareStory(lc: SahLinkedCare): LinkedCareStory {
    const ff = lc.before;
    const names = { client: String(ff?.client?.firstName || 'the client'), partner: String(ff?.partner?.firstName || 'the partner') };
    const entering = sahEnteringPerson(ff);
    const enteringName = entering === 'partner' ? names.partner : names.client;
    const stayingName = entering === 'partner' ? names.client : names.partner;
    const before = itemsOf(lc.before, names);
    const after = itemsOf(lc.after, names);
    const afterByKey = new Map(after.map((a) => [a.key, a]));
    const beforeKeys = new Set(before.map((b) => b.key));
    const deposit = after.filter((a) => a.group === 'radPaid').reduce((t, a) => t + a.value, 0)
        - before.filter((b) => b.group === 'radPaid').reduce((t, b) => t + b.value, 0);
    const used = before
        .filter((b) => b.group !== 'radPaid')
        .map((b) => ({ label: b.label, before: b.value, after: afterByKey.get(b.key)?.value ?? 0 }))
        .filter((r) => r.before - r.after >= 1);
    const added = after
        .filter((a) => a.group !== 'radPaid' && !beforeKeys.has(a.key) && a.value >= 1)
        .map((a) => ({ label: a.label, value: a.value }));
    const left = after.filter((a) => !NOT_LEFT.has(a.group) && a.value >= 1).map((a) => ({ label: a.label, value: a.value }));
    const homeBefore = num((lc.before?.home as { value?: unknown } | undefined)?.value);
    const homeAfter = num((lc.after?.home as { value?: unknown } | undefined)?.value);
    return {
        enteringName,
        stayingName,
        planTitle: savedPlanTitle(lc.record as never, ff),
        whole: lc.whole,
        reason: lc.reason,
        caveats: lc.caveats,
        deposit: Math.max(0, deposit),
        used,
        added,
        debtsCleared: Math.max(0, debtsOf(lc.before) - debtsOf(lc.after)),
        homeSold: homeBefore > 0 && homeAfter < 1,
        fees: Number(lc.details?.annualCost) || 0,
        living: lc.details?.livingExpenses != null ? Number(lc.details.livingExpenses) : null,
        left,
        leftTotal: left.reduce((t, l) => t + l.value, 0),
    };
}

/** The sentence that keeps the deposit in proportion: it is held, not spent (owner, 24 Oct 2026). */
export const depositRefundSentence = (enteringName: string) =>
    `The room deposit is not spent: it is refunded when ${enteringName} leaves care, less anything drawn from it for fees, and until then it still counts in what is left to the family.`;

const Line = ({ label, value, strong, sub }: { label: React.ReactNode; value: React.ReactNode; strong?: boolean; sub?: React.ReactNode }) => (
    <div className="flex items-baseline justify-between gap-4 py-1.5 text-[14px]">
        <span className={strong ? 'font-semibold text-slate-900' : 'text-slate-700'}>{label}{sub && <span className="block text-[12.5px] font-normal text-slate-500">{sub}</span>}</span>
        <span className={`shrink-0 tabular-nums ${strong ? 'font-bold text-brand-navy' : 'text-slate-800'}`}>{value}</span>
    </div>
);

/** Step 2's and the plan page's section. */
export function SahLinkedCareSummary({ linkedCare, testId = 'sah-linked-care', refundNote = true }: { linkedCare: SahLinkedCare; testId?: string; /** False where the page says it under "How long the money lasts". */ refundNote?: boolean }) {
    const s = linkedCareStory(linkedCare);
    return (
        <div data-testid={testId}>
            <p className="text-[15px] leading-relaxed text-slate-700">
                These figures include <strong className="text-slate-900">{s.enteringName}&apos;s chosen aged care plan</strong>, &ldquo;{s.planTitle}&rdquo;,
                {s.whole ? ' carried out in full: everything below has already happened before any Support at home plan is tested.' : <> with only its room deposit, fees and living costs: {s.reason || 'the rest of the plan could not be rebuilt just now.'}</>}
            </p>
            <div className="mt-3 grid gap-x-8 gap-y-4 md:grid-cols-2">
                <div>
                    <p className="text-[13px] font-semibold text-brand-navy">What the aged care plan uses</p>
                    <div className="mt-1 divide-y divide-slate-100 border-y border-slate-100">
                        {s.deposit >= 1 && <Line strong label="Room deposit (RAD) paid" value={money(s.deposit)} />}
                        {s.used.map((u) => (
                            <Line key={u.label} label={u.label} value={<>{money(u.before)} <span className="text-slate-400">→</span> {money(u.after)}</>} />
                        ))}
                        {s.added.map((a) => <Line key={a.label} label={<>{a.label} <span className="text-slate-500">(new)</span></>} value={money(a.value)} />)}
                        {s.debtsCleared >= 1 && <Line label="Debts paid off" value={money(s.debtsCleared)} />}
                        {s.homeSold && <Line label="The home" value="sold" />}
                        {s.fees >= 1 && <Line label="Yearly care fees" sub="daily fee, means-tested fee and any daily accommodation payment, paid from the household each year" value={`${money(s.fees)} a year`} />}
                        {s.living != null && s.living >= 1 && <Line label={`Living costs at home for ${s.stayingName}`} value={`${money(s.living)} a year`} />}
                    </div>
                </div>
                <div>
                    <p className="text-[13px] font-semibold text-brand-navy">What is left for {s.stayingName} at home</p>
                    <div className="mt-1 divide-y divide-slate-100 border-y border-slate-100">
                        {s.left.map((l) => <Line key={l.label} label={l.label} value={money(l.value)} />)}
                        <Line strong label="Savings left" value={money(s.leftTotal)} />
                    </div>
                </div>
            </div>
            {refundNote && s.deposit >= 1 && <p className="mt-3 text-[13.5px] leading-relaxed text-slate-600">{depositRefundSentence(s.enteringName)}</p>}
            {s.caveats.map((c) => <p key={c} className="mt-2 text-[13px] text-slate-500">{c}</p>)}
        </div>
    );
}
